import React, { useState, useEffect } from 'react';
import {
  Phone, Mail, MessageCircle, CheckCircle2, Building2, Package, FileCheck, ClipboardList,
  Warehouse, Shield, Users, Target, Award, Briefcase, TrendingUp, Menu, X, MapPin, Database,
  Store, Factory, ShoppingCart, Building, Landmark, Rocket, ArrowUpRight, Globe2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Toaster } from 'sonner';
import { EnquiryForm } from './EnquiryForm';
import { ClientLogoWall } from './ClientLogos';

const LOGO = '/audix-wordmark.png';
const LOGO_LIGHT = '/audix-wordmark-white.png';

const services = [
  {
    icon: Package,
    title: 'Stock / Inventory Audit',
    description: 'Independent verification of inventory to ensure accuracy, existence, valuation, and effectiveness of inventory controls across warehouses, retail outlets, and distribution centers.',
    image: 'https://images.unsplash.com/photo-1664382953403-fc1ac77073a0',
    link: '/stock-audit-services-delhi'
  },
  {
    icon: FileCheck,
    title: 'Internal Audit',
    description: 'Risk-based internal audits designed to evaluate governance frameworks, strengthen internal controls, and improve operational efficiency.',
    image: 'https://images.unsplash.com/photo-1573164574572-cb89e39749b4',
    link: '/internal-audit-services-delhi'
  },
  {
    icon: Building2,
    title: 'Fixed Asset Verification',
    description: 'Physical verification and reconciliation of fixed assets to ensure accuracy of records, tagging, movement control, and compliance with applicable accounting standards.',
    image: 'https://images.unsplash.com/photo-1753955900083-b62ee8d97805',
    link: '/fixed-asset-verification-delhi'
  },
  {
    icon: ClipboardList,
    title: 'SOP & Process Audit',
    description: 'Review and assessment of standard operating procedures to identify control gaps, inefficiencies, and opportunities for process improvement.',
    image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984',
    link: null
  },
  {
    icon: Warehouse,
    title: 'Warehouse Audit',
    description: 'Comprehensive warehouse audits covering stock movement, storage practices, documentation controls, and loss prevention mechanisms.',
    image: 'https://images.unsplash.com/photo-1749244768351-2726dc23d26c',
    link: null
  },
  {
    icon: Shield,
    title: 'Risk & Compliance Review',
    description: 'Evaluation of compliance risks, control weaknesses, and exposure areas to support informed management decision-making.',
    image: 'https://images.unsplash.com/photo-1752118464988-2914fb27d0f0',
    link: null
  }
];

const manpowerService = {
  icon: Users,
  title: 'Manpower Sourcing (Audit Support Services)',
  description: 'We provide trained audit manpower support to larger audit firms for on-ground audit execution and operational assistance. Our team supports audit engagements by assisting with stock verification, retail and warehouse audits, data validation, MIS support, and process checks, working strictly under the scope and supervision defined by the lead audit firm. This collaboration model allows partner firms to scale audit operations efficiently, while enabling our team to gain structured exposure, hands-on experience, and continuous training in real audit environments. We focus on professional conduct, data confidentiality, and process discipline, ensuring audit work is executed accurately and in line with engagement requirements.',
  image: 'https://images.unsplash.com/photo-1622675363311-3e1904dc1885'
};

const whyChoose = [
  'Founded by professionals with 10+ years of audit experience',
  'GST & MSME Registered audit firm',
  'Team comprising Audit Professionals, CA & CMA backgrounds',
  'Strong on-ground audit execution capabilities',
  'Ethical, independent, and confidential working approach',
  'Experience across multiple locations in India',
  'Trusted execution partner for professional firms and corporates'
];

const industries = [
  { name: 'Retail Chains', icon: Store },
  { name: 'Warehouses & Logistics', icon: Package },
  { name: 'Manufacturing Units', icon: Factory },
  { name: 'E-commerce', icon: ShoppingCart },
  { name: 'FMCG', icon: Building },
  { name: 'Corporates', icon: Landmark },
  { name: 'Startups', icon: Rocket }
];

const approach = [
  { text: 'Structured and systematic', icon: Target, note: 'Defined audit programs, checklists and sign-offs at every stage.' },
  { text: 'Practical and field-oriented', icon: Briefcase, note: 'On-ground execution by teams who have counted real stock.' },
  { text: 'Independent and unbiased', icon: Award, note: 'Objective observations, documented evidence, zero influence.' },
  { text: 'Focused on clear, actionable outcomes', icon: TrendingUp, note: 'Reports management can act on, not just file away.' }
];

const teamMembers = [
  {
    id: 'arvind',
    name: 'Arvind Singh Bora',
    role: 'Founder & Lead Auditor',
    image: 'https://customer-assets.emergentagent.com/job_company-profile-52/artifacts/qwj5paje_My%20own%20Pic.jpeg',
    experience: 'Graduate with 10+ years of extensive experience across multiple leading audit firms including Future Group and NHBS. Specialized in execution-driven audit solutions, inventory management, and fixed asset verification with proven track record in managing large-scale multi-location audit engagements.',
    expertise: ['Stock & Inventory Audit', 'Fixed Asset Verification', 'Internal Audit', 'Warehouse Audit', 'Process Audit', 'Multi-location Audits']
  },
  {
    id: 'vikas',
    name: 'Vikas Punia',
    role: 'CA Professional Advisor',
    image: 'https://customer-assets.emergentagent.com/job_company-profile-52/artifacts/wm7jfmgm_Vikas%20%20punia%20ca%20Pic.jpeg',
    experience: 'A finance and audit professional with 6+ years of experience, including Big 4 firm exposure, with strong expertise across assurance, advisory, and compliance functions. Specialized in statutory audits, ICFR, IFRS, US GAAP process reviews, and IPO listing support.',
    expertise: ['Statutory & Tax Audits', 'ICFR & IFRS', 'Due Diligence', 'Virtual CFO Services', 'IPO Listing Support', 'Global Accounting']
  }
];

const navLinks = [
  { label: 'Services', href: '#services', id: 'services' },
  { label: 'About', href: '#about', id: 'about' },
  { label: 'Team', href: '#team', id: 'team' },
  { label: 'Free Quote', href: '#quote', id: 'quote' },
  { label: 'Contact', href: '#contact', id: 'contact' }
];

const SectionHeading = ({ overline, title, subtitle, align = 'left', tone = 'dark', testId }) => (
  <div
    className={`${align === 'center' ? 'text-center mx-auto max-w-3xl' : 'max-w-3xl'} mb-12 md:mb-16`}
    data-testid={testId}
  >
    <p className={`text-xs font-bold uppercase tracking-[0.24em] mb-4 ${tone === 'light' ? 'text-[#b78d2f]' : 'text-[#8a6a1f]'}`}>
      {overline}
    </p>
    <h2 className={`ax-display text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.08] ${tone === 'light' ? 'text-white' : 'text-[#0f2038]'}`}>
      {title}
    </h2>
    <div className={`ax-rule mt-6 ${align === 'center' ? 'mx-auto' : ''}`} />
    {subtitle && (
      <p className={`mt-6 text-base md:text-lg leading-relaxed ${tone === 'light' ? 'text-slate-300' : 'text-slate-600'}`}>
        {subtitle}
      </p>
    )}
  </div>
);

const BrandLockup = ({ variant = 'dark', size = 'header' }) => {
  const isLight = variant === 'light';
  return (
    <div className="flex min-w-0 items-center gap-2.5 sm:gap-3 md:gap-4" data-testid="brand-lockup">
      <img
        src={isLight ? LOGO_LIGHT : LOGO}
        alt="AudiX Solutions & Co. logo"
        className={`${size === 'header' ? 'h-6 sm:h-7 md:h-9' : 'h-8'} w-auto shrink-0`}
        data-testid="brand-logo-image"
      />
      <span className={`h-8 sm:h-9 md:h-11 w-px shrink-0 ${isLight ? 'bg-white/25' : 'bg-slate-200'}`} />
      <div className="min-w-0">
        <p
          className={`ax-display whitespace-nowrap text-[13px] sm:text-base md:text-lg font-extrabold leading-tight ${isLight ? 'text-white' : 'text-[#0f2038]'}`}
          data-testid="brand-company-name"
        >
          Solutions &amp; Co.
        </p>
        <p className={`hidden sm:block whitespace-nowrap text-[10px] md:text-[11px] font-semibold uppercase tracking-[0.16em] ${isLight ? 'text-[#d8b45f]' : 'text-[#8a6a1f]'}`}>
          Audit • Assurance • Advisory
        </p>
      </div>
    </div>
  );
};

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleCall = () => { window.location.href = 'tel:9205514887'; };
  const handleWhatsApp = () => { window.open('https://wa.me/919205514887', '_blank'); };
  const handleEmail = () => { window.location.href = 'mailto:arvindsingh@audix.co.in'; };

  return (
    <div className="audix-home min-h-screen" data-testid="home-page">
      {/* ================= HEADER ================= */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/92 backdrop-blur-xl border-b border-slate-200 shadow-[0_8px_30px_-20px_rgba(15,32,56,0.45)]'
            : 'bg-white/80 backdrop-blur-md border-b border-slate-200/70'
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between gap-3 py-3 md:py-4">
            <a href="#top" className="min-w-0 flex-1 lg:flex-none" data-testid="header-brand-link">
              <BrandLockup />
            </a>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((l) => (
                <a
                  key={l.id}
                  href={l.href}
                  className="ax-nav-link text-[15px] font-semibold text-slate-700 hover:text-[#0f2038]"
                  data-testid={`nav-${l.id}-link`}
                >
                  {l.label}
                </a>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <a
                href="/audit"
                className="inline-flex items-center gap-2 rounded-full border border-[#0f2038]/15 bg-white px-5 py-2.5 text-sm font-bold text-[#0f2038] transition-all duration-200 hover:border-[#0f2038]/40 hover:bg-slate-50 active:scale-[0.98]"
                data-testid="header-data-management-link"
              >
                <Database className="h-4 w-4 text-emerald-600" />
                Data Management
              </a>
              <button
                onClick={handleCall}
                className="inline-flex items-center gap-2 rounded-full bg-[#0f2038] px-6 py-2.5 text-sm font-bold text-white shadow-[0_10px_24px_-12px_rgba(15,32,56,0.9)] transition-all duration-200 hover:bg-[#16304f] active:scale-[0.98]"
                data-testid="header-call-button"
              >
                <Phone className="h-4 w-4" />
                9205514887
              </button>
            </div>

            {/* Mobile toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden shrink-0 rounded-xl border border-slate-200 p-2.5 text-[#0f2038] transition-colors hover:bg-slate-50"
              aria-label="Toggle navigation"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-5 shadow-xl" data-testid="mobile-menu">
            <div className="space-y-1">
              {navLinks.map((l) => (
                <a
                  key={l.id}
                  href={l.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block rounded-lg px-3 py-3 text-base font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-[#0f2038]"
                  data-testid={`mobile-nav-${l.id}-link`}
                >
                  {l.label}
                </a>
              ))}
            </div>
            <a
              href="/audit"
              className="mt-3 flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-bold text-[#0f2038]"
              data-testid="mobile-data-management-link"
            >
              <Database className="h-4 w-4 text-emerald-600" />
              Data Management
            </a>
            <button
              onClick={handleCall}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-[#0f2038] px-4 py-3 font-bold text-white"
              data-testid="mobile-call-button"
            >
              <Phone className="h-4 w-4" />
              Call 9205514887
            </button>
          </div>
        )}
      </header>

      {/* ================= HERO ================= */}
      <section id="top" className="relative overflow-hidden bg-white pt-28 md:pt-36 pb-16 md:pb-24" data-testid="hero-section">
        <div className="ax-grid-bg absolute inset-0 opacity-70" />
        <div className="pointer-events-none absolute -top-40 -right-24 h-[520px] w-[520px] rounded-full bg-[#dbe7fb] blur-[120px] opacity-70" />
        <div className="pointer-events-none absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-[#f3ead1] blur-[120px] opacity-70" />

        <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-7 ax-rise">
              <div className="inline-flex flex-wrap items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-4 py-2 shadow-sm" data-testid="hero-badge">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.14em] text-slate-600">
                  Audit • Assurance • Inventory Solutions • Fixed Asset Audits
                </span>
              </div>

              <h1 className="ax-display mt-7 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.03] text-[#0f2038]" data-testid="hero-title">
                Audit Firm in Delhi
                <span className="block text-[#1d4ed8]">&amp; North India</span>
              </h1>

              <h2 className="mt-5 text-base md:text-lg font-semibold text-slate-700" data-testid="hero-subtitle">
                Stock Audit, Internal Audit &amp; Fixed Asset Verification Services
              </h2>

              <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-slate-600">
                AudiX Solutions &amp; Co. is a professional{' '}
                <Link to="/audit-firm-in-delhi" className="font-semibold text-[#1d4ed8] underline decoration-[#1d4ed8]/30 underline-offset-4 hover:decoration-[#1d4ed8]" data-testid="hero-audit-firm-link">
                  audit firm in Delhi
                </Link>{' '}
                providing stock audit, internal audit, inventory verification, and fixed asset audit services across Delhi NCR and North India.
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-3">
                <button
                  onClick={handleCall}
                  className="group inline-flex items-center gap-2 rounded-full bg-[#0f2038] px-7 py-3.5 text-sm md:text-base font-bold text-white shadow-[0_16px_36px_-18px_rgba(15,32,56,0.9)] transition-all duration-200 hover:bg-[#16304f] active:scale-[0.98]"
                  data-testid="hero-call-button"
                >
                  <Phone className="h-4 w-4" />
                  Call Us
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <button
                  onClick={handleWhatsApp}
                  className="inline-flex items-center gap-2 rounded-full border border-emerald-600/25 bg-emerald-50 px-7 py-3.5 text-sm md:text-base font-bold text-emerald-800 transition-all duration-200 hover:bg-emerald-100 active:scale-[0.98]"
                  data-testid="hero-whatsapp-button"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </button>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-slate-200 pt-7">
                <button onClick={handleCall} className="flex items-center gap-2 text-sm font-semibold text-slate-700 transition-colors hover:text-[#0f2038]" data-testid="hero-phone-chip">
                  <Phone className="h-4 w-4 text-[#1d4ed8]" /> 9205514887
                </button>
                <button onClick={handleEmail} className="flex items-center gap-2 text-sm font-semibold text-slate-700 transition-colors hover:text-[#0f2038]" data-testid="hero-email-chip">
                  <Mail className="h-4 w-4 text-[#1d4ed8]" /> arvindsingh@audix.co.in
                </button>
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-700" data-testid="hero-coverage-chip">
                  <MapPin className="h-4 w-4 text-[#b78d2f]" /> Delhi | Delhi NCR | Pan-India
                </span>
              </div>
            </div>

            <div className="lg:col-span-5" data-testid="hero-visual">
              <div className="relative">
                <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_40px_80px_-50px_rgba(15,32,56,0.55)]">
                  <img
                    src="https://images.unsplash.com/photo-1606836591695-4d58a73eba1e"
                    alt="AudiX audit professionals at work"
                    className="h-[300px] w-full object-cover md:h-[420px]"
                  />
                </div>
                <div className="absolute -bottom-8 left-4 right-4 grid grid-cols-3 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 shadow-[0_24px_48px_-28px_rgba(15,32,56,0.5)]">
                  {[
                    { v: '10+', l: 'Years Experience' },
                    { v: '16+', l: 'Trusted Clients' },
                    { v: '100%', l: 'Compliance' }
                  ].map((s) => (
                    <div key={s.l} className="bg-white px-3 py-4 text-center" data-testid={`hero-stat-${s.l.toLowerCase().replace(/\s+/g, '-')}`}>
                      <p className="ax-display text-xl md:text-2xl font-extrabold text-[#0f2038]">{s.v}</p>
                      <p className="mt-1 text-[10px] md:text-xs font-semibold uppercase tracking-wider text-slate-500">{s.l}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= WHO WE ARE ================= */}
      <section id="about" className="scroll-mt-24 bg-[#f7f8fa] py-20 md:py-28" data-testid="about-section">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <SectionHeading overline="Who We Are" title="Audit expertise built on a decade in the field" testId="about-heading" />
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1769740333462-9a63bfa914bc?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                  alt="AudiX professional audit team"
                  className="h-[280px] w-full rounded-3xl border border-slate-200 object-cover shadow-[0_30px_60px_-40px_rgba(15,32,56,0.5)] md:h-[360px]"
                />
                <div className="ax-dot-bg absolute -bottom-5 -right-5 -z-10 h-32 w-32 rounded-2xl opacity-60" />
              </div>
            </div>

            <div className="lg:col-span-7 lg:pt-4">
              <div className="rounded-2xl border border-slate-200 border-l-4 border-l-[#1d4ed8] bg-white p-6 md:p-8">
                <p className="text-base md:text-lg leading-relaxed text-slate-700">
                  AudiX Solutions &amp; Co. is a trusted{' '}
                  <Link to="/audit-firm-in-delhi" className="font-semibold text-[#1d4ed8] hover:underline" data-testid="about-audit-firm-link">audit firm in Delhi</Link>{' '}
                  specializing in{' '}
                  <Link to="/stock-audit-services-delhi" className="font-semibold text-[#1d4ed8] hover:underline" data-testid="about-stock-audit-link">stock audit</Link>,{' '}
                  <Link to="/internal-audit-services-delhi" className="font-semibold text-[#1d4ed8] hover:underline" data-testid="about-internal-audit-link">internal audit</Link>, and{' '}
                  <Link to="/fixed-asset-verification-delhi" className="font-semibold text-[#1d4ed8] hover:underline" data-testid="about-fixed-asset-link">fixed asset verification</Link>{' '}
                  services for corporates, retail chains, and multi-location businesses across North India.
                </p>
              </div>

              <div className="mt-8 space-y-6 text-base leading-relaxed text-slate-600">
                <p>
                  AudiX Solutions &amp; Co. is an audit and advisory firm founded by a seasoned audit professional with more than a decade of hands-on experience in audit, assurance, inventory management, and fixed asset verification.
                </p>
                <p>
                  Although AudiX is a newly established firm, its foundation is built on deep domain expertise, field-level execution strength, and structured audit methodologies developed over years of professional practice. Our team brings practical exposure across diverse audit environments and industries, enabling us to deliver audits that are not only compliant but also value-focused.
                </p>
                <p>
                  We work closely with clients to strengthen internal controls, enhance operational efficiency, and provide clear, actionable audit insights aligned with professional standards.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= WHY CHOOSE ================= */}
      <section className="relative overflow-hidden bg-white py-20 md:py-28" data-testid="why-choose-section">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-4">
              <SectionHeading
                overline="Your Trusted Audit Partner"
                title="Why Choose AudiX"
                subtitle="Experience the difference of working with audit professionals who combine expertise, integrity and execution excellence."
                testId="why-choose-heading"
              />
              <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200">
                {[
                  { v: '10+', l: 'Years Experience' },
                  { v: '16+', l: 'Trusted Clients' },
                  { v: '100%', l: 'Compliance' },
                  { v: 'Pan-India', l: 'Coverage' }
                ].map((s) => (
                  <div key={s.l} className="bg-white px-5 py-6" data-testid={`why-stat-${s.l.toLowerCase().replace(/\s+/g, '-')}`}>
                    <p className="ax-display text-2xl font-extrabold text-[#0f2038]">{s.v}</p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{s.l}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="grid gap-5 sm:grid-cols-2">
                {whyChoose.map((item, index) => (
                  <div
                    key={index}
                    className="ax-card group flex items-start gap-4 rounded-2xl p-6"
                    data-testid={`why-choose-card-${index}`}
                  >
                    <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f3ead1] transition-colors duration-300 group-hover:bg-[#e8d9ae]">
                      <CheckCircle2 className="h-5 w-5 text-[#8a6a1f]" strokeWidth={2.4} />
                    </span>
                    <p className="text-[15px] font-semibold leading-relaxed text-[#0f2038]">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= SERVICES ================= */}
      <section id="services" className="scroll-mt-24 bg-[#f7f8fa] py-20 md:py-28" data-testid="services-section">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <SectionHeading
            overline="Our Services"
            title="Comprehensive audit solutions, executed on the ground"
            subtitle="Seven service lines covering inventory, assets, process and compliance — delivered by payroll audit professionals."
            testId="services-heading"
          />

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {services.map((service, index) => {
              const CardWrapper = service.link ? Link : 'div';
              const cardProps = service.link ? { to: service.link } : {};
              return (
                <CardWrapper
                  key={index}
                  {...cardProps}
                  className="ax-card group flex h-full flex-col overflow-hidden rounded-2xl"
                  data-testid={`service-card-${index}`}
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0f2038]/45 via-transparent to-transparent" />
                    <span className="absolute bottom-4 left-4 flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-md">
                      <service.icon className="h-5 w-5 text-[#1d4ed8]" />
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="ax-display text-lg font-bold text-[#0f2038]">{service.title}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{service.description}</p>
                    {service.link && (
                      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-[#1d4ed8]">
                        Learn more
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    )}
                  </div>
                </CardWrapper>
              );
            })}
          </div>

          {/* Manpower sourcing — featured landscape */}
          <div className="ax-card mt-6 overflow-hidden rounded-2xl lg:mt-8" data-testid="service-card-manpower">
            <div className="grid md:grid-cols-12">
              <div className="relative h-60 md:h-auto md:col-span-4">
                <img
                  src={manpowerService.image}
                  alt={manpowerService.title}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="md:col-span-8 p-7 md:p-10">
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#eaf0fb]">
                    <manpowerService.icon className="h-6 w-6 text-[#1d4ed8]" />
                  </span>
                  <h3 className="ax-display text-xl md:text-2xl font-bold text-[#0f2038]">{manpowerService.title}</h3>
                </div>
                <p className="mt-5 text-sm md:text-base leading-relaxed text-slate-600">{manpowerService.description}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= SEO: DELHI NCR ================= */}
      <section className="bg-white py-20 md:py-24" data-testid="seo-delhi-section">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <div className="rounded-3xl border border-slate-200 border-l-4 border-l-[#b78d2f] bg-[#f7f8fa] p-7 md:p-12">
            <h2 className="ax-display text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#0f2038]">
              Audit Services in Delhi &amp; Delhi NCR
            </h2>
            <p className="mt-6 max-w-4xl text-base md:text-lg leading-relaxed text-slate-600">
              As an experienced{' '}
              <Link to="/audit-firm-in-delhi" className="font-semibold text-[#1d4ed8] hover:underline" data-testid="seo-audit-firm-link">audit firm in Delhi</Link>, AudiX provides professional audit and assurance services across Delhi NCR, including Gurugram, Noida, Faridabad, and Ghaziabad. Our services include{' '}
              <Link to="/stock-audit-services-delhi" className="font-semibold text-[#1d4ed8] hover:underline" data-testid="seo-stock-audit-link">stock audits</Link>,{' '}
              <Link to="/internal-audit-services-delhi" className="font-semibold text-[#1d4ed8] hover:underline" data-testid="seo-internal-audit-link">internal audits</Link>, warehouse audits, and{' '}
              <Link to="/fixed-asset-verification-delhi" className="font-semibold text-[#1d4ed8] hover:underline" data-testid="seo-fixed-asset-link">fixed asset verification</Link>{' '}
              for businesses across North India.
            </p>
            <div className="mt-9 grid gap-4 md:grid-cols-3">
              {[
                { icon: MapPin, t: 'Delhi NCR Coverage', s: 'Gurugram, Noida, Faridabad, Ghaziabad' },
                { icon: Award, t: 'Professional Standards', s: 'GST & MSME Registered' },
                { icon: Users, t: 'Expert Team', s: 'CA & CMA Professionals' }
              ].map((c, i) => (
                <div key={i} className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid={`seo-info-chip-${i}`}>
                  <c.icon className="mt-0.5 h-5 w-5 shrink-0 text-[#1d4ed8]" />
                  <div>
                    <p className="font-bold text-[#0f2038]">{c.t}</p>
                    <p className="mt-1 text-sm text-slate-600">{c.s}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= MANPOWER & VENDOR SUPPORT ================= */}
      <section className="bg-[#0f2038] py-20 md:py-28" data-testid="manpower-vendor-section">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <SectionHeading
            overline="Execution Model"
            title="Audit Manpower & Vendor Support"
            tone="light"
            subtitle="No per-day resources. Only trained, payroll-based audit professionals — for our own engagements and for partner firms."
            testId="manpower-heading"
          />
          <div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
            <div className="rounded-2xl border border-white/12 bg-white/[0.06] p-7 md:p-9 backdrop-blur-sm" data-testid="payroll-manpower-panel">
              <h3 className="ax-display text-xl md:text-2xl font-bold text-white">Dedicated Payroll-Based Audit Manpower</h3>
              <div className="ax-rule mt-5" />
              <p className="mt-6 leading-relaxed text-slate-300">
                At AudiX Solutions &amp; Co., we do not engage or deploy per-day manpower for audit assignments. All audit work is executed exclusively through experienced audit professionals who are on our payroll, ensuring consistency, accountability, and execution quality across all engagements.
              </p>
              <p className="mt-4 leading-relaxed text-slate-300">
                We believe audit execution demands trained, disciplined, and stable teams, not temporary resources engaged for short-term cost optimization. Our payroll-based model allows better control, continuity, and reliable outcomes for clients and partner firms.
              </p>
            </div>
            <div className="rounded-2xl border border-white/12 bg-white/[0.06] p-7 md:p-9 backdrop-blur-sm" data-testid="vendor-support-panel">
              <h3 className="ax-display text-xl md:text-2xl font-bold text-white">Vendor Support for Large Audit Firms</h3>
              <div className="ax-rule mt-5" />
              <p className="mt-6 leading-relaxed text-slate-300">
                AudiX also works as a trusted execution partner for large audit firms and professional practices. We provide structured on-ground audit support while strictly following the principal firm's audit methodology, reporting formats, timelines, and confidentiality requirements.
              </p>
              <div className="mt-7 space-y-3">
                {[
                  'No per-day or ad-hoc manpower',
                  '100% payroll-based experienced audit professionals',
                  'Reduced supervision and rework',
                  'Reliable execution for large and multi-location audits',
                  'Professional independence and confidentiality maintained'
                ].map((p, i) => (
                  <div key={i} className="flex items-start gap-3" data-testid={`vendor-point-${i}`}>
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#d8b45f]" />
                    <span className="text-sm md:text-base text-slate-200">{p}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= INDUSTRIES ================= */}
      <section className="bg-white py-20 md:py-28" data-testid="industries-section">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <SectionHeading overline="Sectors" title="Industries We Serve" testId="industries-heading" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="ax-card group flex flex-col items-start gap-4 rounded-2xl p-5"
                data-testid={`industry-card-${index}`}
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#eaf0fb] transition-colors duration-300 group-hover:bg-[#0f2038]">
                  <industry.icon className="h-5 w-5 text-[#1d4ed8] transition-colors duration-300 group-hover:text-white" />
                </span>
                <p className="text-sm font-bold leading-snug text-[#0f2038]">{industry.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CLIENTS ================= */}
      <section className="bg-[#f7f8fa] py-20 md:py-28" data-testid="clients-section">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <SectionHeading
            overline="Our Esteemed Clients"
            title="Trusted by leading brands across India"
            subtitle="Reliable audit and assurance support for retail, lifestyle and multi-location businesses."
            testId="clients-heading"
          />
          <ClientLogoWall />

          <div className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 md:grid-cols-3">
            {[
              { v: '16+', l: 'Trusted Client Relationships' },
              { v: '100%', l: 'Client Satisfaction Rate' },
              { v: 'Pan-India', l: 'Service Coverage' }
            ].map((s, i) => (
              <div key={i} className="bg-white px-6 py-8" data-testid={`client-trust-stat-${i}`}>
                <p className="ax-display text-3xl font-extrabold text-[#0f2038]">{s.v}</p>
                <p className="mt-2 text-sm font-medium text-slate-600">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= APPROACH ================= */}
      <section className="bg-white py-20 md:py-28" data-testid="approach-section">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <SectionHeading
            overline="How We Work"
            title="Our Approach"
            subtitle="We believe audits should go beyond compliance and deliver practical value."
            testId="approach-heading"
          />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {approach.map((item, index) => (
              <div key={index} className="ax-card rounded-2xl p-7" data-testid={`approach-card-${index}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0f2038]">
                  <item.icon className="h-6 w-6 text-[#d8b45f]" />
                </span>
                <p className="ax-display mt-6 text-lg font-bold leading-snug text-[#0f2038]">{item.text}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TEAM ================= */}
      <section id="team" className="scroll-mt-24 bg-[#f7f8fa] py-20 md:py-28" data-testid="team-section">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <SectionHeading
            overline="Leadership"
            title="Our Leadership Team"
            subtitle="Meet the professionals driving excellence in audit and assurance services."
            testId="team-heading"
          />
          <div className="space-y-10 md:space-y-14">
            {teamMembers.map((member) => (
              <div key={member.id} className="grid gap-8 lg:grid-cols-12 lg:gap-12" data-testid={`team-member-${member.id}`}>
                <div className="lg:col-span-4">
                  <div className="relative">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="h-[340px] w-full rounded-3xl border border-slate-200 object-cover shadow-[0_30px_60px_-40px_rgba(15,32,56,0.55)] md:h-[380px]"
                      style={{ objectPosition: 'center 20%' }}
                    />
                    <div className="ax-dot-bg absolute -bottom-4 -left-4 -z-10 h-28 w-28 rounded-2xl opacity-60" />
                  </div>
                </div>
                <div className="lg:col-span-8">
                  <h3 className="ax-display text-2xl md:text-3xl font-extrabold text-[#0f2038]">{member.name}</h3>
                  <p className="mt-2 inline-flex items-center rounded-full bg-[#eaf0fb] px-4 py-1.5 text-sm font-bold text-[#1d4ed8]">
                    {member.role}
                  </p>
                  <div className="ax-rule mt-5" />
                  <p className="mt-6 text-base leading-relaxed text-slate-600">{member.experience}</p>
                  <p className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-[#8a6a1f]">Areas of Expertise</p>
                  <div className="mt-4 flex flex-wrap gap-2.5">
                    {member.expertise.map((skill, idx) => (
                      <span
                        key={idx}
                        className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-[#0f2038] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#0f2038]/30 hover:shadow-md"
                        data-testid={`team-${member.id}-skill-${idx}`}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CONTACT ================= */}
      <section id="contact" className="scroll-mt-24 relative overflow-hidden bg-white py-20 md:py-28" data-testid="contact-section">
        <div className="ax-grid-bg absolute inset-0 opacity-60" />
        <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <SectionHeading
            overline="Get In Touch"
            title="Let's discuss your audit requirement"
            align="center"
            subtitle="Connect with us via call or WhatsApp to discuss your audit and manpower support requirements."
            testId="contact-heading"
          />
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
            <button onClick={handleCall} className="ax-card rounded-2xl p-8 text-left" data-testid="contact-phone-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0f2038]">
                <Phone className="h-5 w-5 text-white" />
              </span>
              <p className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Phone</p>
              <p className="ax-display mt-2 text-lg font-extrabold text-[#0f2038]">9205514887</p>
            </button>
            <button onClick={handleEmail} className="ax-card rounded-2xl p-8 text-left" data-testid="contact-email-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#1d4ed8]">
                <Mail className="h-5 w-5 text-white" />
              </span>
              <p className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Email</p>
              <p className="ax-display mt-2 break-all text-lg font-extrabold text-[#0f2038]">arvindsingh@audix.co.in</p>
            </button>
            <button onClick={handleWhatsApp} className="ax-card rounded-2xl p-8 text-left" data-testid="contact-whatsapp-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-600">
                <MessageCircle className="h-5 w-5 text-white" />
              </span>
              <p className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">WhatsApp</p>
              <p className="ax-display mt-2 text-lg font-extrabold text-[#0f2038]">Chat with us</p>
            </button>
          </div>
          <div className="mt-12 text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Visit our website</p>
            <a
              href="http://www.audix.co.in"
              target="_blank"
              rel="noopener noreferrer"
              className="ax-display mt-3 inline-flex items-center gap-2 text-xl font-extrabold text-[#1d4ed8] transition-colors hover:text-[#0f2038]"
              data-testid="website-link"
            >
              <Globe2 className="h-5 w-5" />
              www.audix.co.in
            </a>
          </div>
        </div>
      </section>

      {/* ================= ENQUIRY FORM ================= */}
      <EnquiryForm />

      {/* ================= FOOTER ================= */}
      <footer className="bg-[#0b1728] px-4 py-14 text-white sm:px-6 lg:px-10" data-testid="site-footer">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid gap-10 md:grid-cols-3">
            <div>
              <BrandLockup variant="light" size="footer" />
              <p className="mt-5 max-w-xs text-sm leading-relaxed text-slate-400">
                Where Audits Meet Exceptional X-ceptionalism
              </p>
            </div>
            <div>
              <h4 className="ax-display text-sm font-bold uppercase tracking-[0.18em] text-[#d8b45f]">Quick Links</h4>
              <div className="mt-5 space-y-3">
                {navLinks.map((l) => (
                  <a
                    key={l.id}
                    href={l.href}
                    className="block text-sm text-slate-400 transition-colors hover:text-white"
                    data-testid={`footer-${l.id}-link`}
                  >
                    {l.label}
                  </a>
                ))}
                <a href="/audit" className="block text-sm text-slate-400 transition-colors hover:text-white" data-testid="footer-data-management-link">
                  Data Management
                </a>
              </div>
            </div>
            <div>
              <h4 className="ax-display text-sm font-bold uppercase tracking-[0.18em] text-[#d8b45f]">Contact Info</h4>
              <div className="mt-5 space-y-3 text-sm text-slate-400">
                <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#d8b45f]" /> 9205514887</p>
                <p className="flex items-center gap-2 break-all"><Mail className="h-4 w-4 text-[#d8b45f]" /> arvindsingh@audix.co.in</p>
                <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#d8b45f]" /> Delhi | Delhi NCR | North Zone | Pan-India</p>
              </div>
            </div>
          </div>
          <div className="mt-12 border-t border-white/10 pt-6">
            <p className="text-xs text-slate-500">© 2026 AudiX Solutions &amp; Co. All rights reserved.</p>
          </div>
        </div>
      </footer>
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default Home;
